import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Loader,
  NotificationType,
  Tab,
} from "@transfr-inc/dashboard-components";
import {
  AddActivityModal,
  CreateCourseStructureModal,
  DeletionModal,
  EditLessonModal,
  EditLocation,
  EditProgramStructureOrderModal,
  EditUnitModal,
  ProgramActivityLogModal,
  ProgramStructureSection,
  ResetCourseModal,
} from "@transfr-inc/dashboard-components/curriculum";

import { useContainer } from "@contexts/container-context";
import { useNotificationContext } from "@contexts/notification-context";
import { useApiRequest } from "@lib/hooks";
import { useAiCurriculumEnabled, useCurrentUser } from "@queries";

import { ClassrooomCourseBuilderGuard } from "../components";

import "./classroom.course-builder.tab.scss";

export const ClassroomCourseBuilderTab = ({
  name,
  className,
  courseData,
  loading,
  readOnly,
  classroomOrgCode,
  classroomId,
  onCourseBuilt,
}) => {
  const { courseService } = useContainer();
  const currentUser = useCurrentUser();
  const { addNotification } = useNotificationContext();
  const { data: aiCurriculumFlag, isFetching: fetchingAICurriculumFlag } =
    useAiCurriculumEnabled();
  const aiCurriculumEnabled = !!aiCurriculumFlag?.enabled;

  const [localCourseData, setLocalCourseData] = useState(courseData);
  const [isSaving, setIsSaving] = useState(false);
  const [courseMainStructure, setCourseMainStructure] = useState();
  const [selectedStructureType, setSelectedStructureType] = useState();
  const [parentId, setParentId] = useState();
  const [newlyCreatedItemId, setNewlyCreatedItemId] = useState();
  const [selectedStructureData, setSelectedStructureData] = useState();
  const [showCreateStructureModal, setShowCreateStructureModal] =
    useState(false);
  const [showEditStructureOrderModal, setShowEditStructureOrderModal] =
    useState(false);
  const [showEditUnitModal, setShowEditUnitModal] = useState(false);
  const [showEditLessonModal, setShowEditLessonModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [addActivityLesson, setAddActivityLesson] = useState();
  const [addActivityContentType, setAddActivityContentType] = useState();
  const [deleteState, setDeleteState] = useState({ open: false });
  const [showResetModal, setShowResetModal] = useState(false);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [showInitialView, setShowInitialView] = useState(true);

  const courseId = localCourseData?.id;

  useEffect(() => {
    if (courseData) {
      setLocalCourseData(courseData);

      let courseMainStructureValue;
      if (courseData.units?.length > 0) {
        courseMainStructureValue = "units";
      } else if (courseData.lessons?.length > 0) {
        courseMainStructureValue = "lessons";
      }

      setCourseMainStructure(courseMainStructureValue);
      setShowInitialView(!courseMainStructureValue);
    }
  }, [courseData]);

  const { response: allActivities, sendRequest: fetchAllActivities } =
    useApiRequest(
      () => courseService.getOrgActivities(classroomOrgCode),
      false
    );

  const {
    response: allCourseActivities,
    sendRequest: fetchAllCourseActivities,
  } = useApiRequest(() => courseService.getCourseActivities(courseId), false);

  const {
    response: activityLogs,
    sendRequest: getCourseActivityLogs,
    loading: activityLogsLoading,
  } = useApiRequest(() => courseService.getCourseActivityLogs(courseId), false);

  useEffect(() => {
    if (readOnly || !courseId) return;
    fetchAllActivities();
    fetchAllCourseActivities();
  }, [readOnly, courseId]);

  const getMappedActivitiesForLesson = useCallback(
    (lessonActivities = []) => {
      if (!allActivities) return [];
      const courseActivityIds = new Set(
        allCourseActivities?.map((a) => a.contentId) || []
      );
      const lessonActivityIds = new Set(
        lessonActivities?.map((a) => a.id || a.data?.id) || []
      );
      const allDisabledIds = new Set([
        ...courseActivityIds,
        ...lessonActivityIds,
      ]);
      return allActivities.map((activity) => ({
        ...activity,
        disabled: allDisabledIds.has(activity.id),
      }));
    },
    [allActivities, allCourseActivities]
  );

  // Content types available to add, derived from the org's activities — drives
  // the "Add Activity" menu options on each lesson header.
  const activityContentTypes = useMemo(
    () => [...new Set((allActivities || []).map((a) => a.contentType))],
    [allActivities]
  );

  const refreshEditData = async () => {
    await fetchAllCourseActivities();
  };

  const onAddActivity = (lesson, contentType) => {
    setAddActivityLesson(lesson);
    setAddActivityContentType(contentType);
    setShowAddActivityModal(true);
  };

  const onAddActivities = async (selectedActivities) => {
    setIsSaving(true);
    const lessonId = addActivityLesson?.id;
    const mergeIntoLesson = (lesson) => ({
      ...lesson,
      activities: [...(lesson.activities || []), ...selectedActivities].map(
        (a) => {
          const data = a?.data || a;
          if (!data.contentId) data.contentId = data.id;
          return data;
        }
      ),
    });

    const updatedCourse =
      courseMainStructure === "lessons"
        ? {
            ...localCourseData,
            lessons: localCourseData.lessons.map((lesson) =>
              lesson.id === lessonId ? mergeIntoLesson(lesson) : lesson
            ),
          }
        : {
            ...localCourseData,
            units: localCourseData.units.map((unit) => ({
              ...unit,
              lessons: unit.lessons.map((lesson) =>
                lesson.id === lessonId ? mergeIntoLesson(lesson) : lesson
              ),
            })),
          };

    try {
      const updated = await courseService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditLessonModal.id,
        course: updatedCourse,
        sourceClassroomId: classroomId,
      });
      setLocalCourseData(updated);
      await refreshEditData();
      addNotification({
        type: NotificationType.success,
        message:
          selectedActivities.length > 1
            ? "Activities added."
            : "Activity added.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Error adding activities.",
      });
    } finally {
      setIsSaving(false);
      setShowAddActivityModal(false);
      setAddActivityLesson();
    }
  };

  const onModifyCourseStructure = (
    structureType,
    modifyType,
    isTopLevel,
    structureData
  ) => {
    if (modifyType === "add") {
      setSelectedStructureType(structureType);
      setShowCreateStructureModal(true);
    } else if (modifyType === "edit" && isTopLevel) {
      setSelectedStructureType(structureType);
      setShowEditStructureOrderModal(true);
    } else if (modifyType === "edit" && structureType === "unit") {
      setSelectedStructureType(structureType);
      setSelectedStructureData(structureData);
      setShowEditUnitModal(true);
    } else if (modifyType === "edit" && structureType === "lesson") {
      setSelectedStructureType(structureType);
      setSelectedStructureData(structureData);
      setShowEditLessonModal(true);
    }
  };

  const onApplyStructureOrderChanges = async (items) => {
    setIsSaving(true);
    const payload = {
      editedBy: currentUser.userId,
      editedFrom: EditLocation.EditProgramStructureOrderModal.id,
      course: { ...localCourseData, [selectedStructureType]: items },
      sourceClassroomId: classroomId,
    };
    try {
      const updated = await courseService.sendProgramInfo(payload);
      setLocalCourseData(updated);
      await refreshEditData();
      addNotification({
        type: NotificationType.success,
        message:
          selectedStructureType === "units"
            ? "Unit order updated."
            : "Lesson updated.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: `Error updating ${selectedStructureType} order.`,
      });
    } finally {
      setIsSaving(false);
      setShowEditStructureOrderModal(false);
    }
  };

  const onCreateCourseStructure = async (formData) => {
    setIsSaving(true);
    const awardIds = formData.awardIds?.id
      ? [{ id: formData.awardIds.id, type: formData.awardType }]
      : [];
    const activities = (formData.activities || [])
      .flat()
      .map((a) => {
        if (!a?.data) return;
        if (!a.data.contentId) a.data.contentId = a.data.id;
        return a.data;
      })
      .filter(Boolean);
    formData.activities = activities;

    const editLocation =
      selectedStructureType === "units"
        ? EditLocation.CreateUnitModal.id
        : EditLocation.CreateLessonModal.id;

    const updatedCourse = parentId
      ? {
          ...localCourseData,
          units: localCourseData.units.map((unit) =>
            unit.id === parentId
              ? {
                  ...unit,
                  [selectedStructureType]: [
                    ...(unit[selectedStructureType] || []),
                    { ...formData, awardIds },
                  ],
                }
              : unit
          ),
        }
      : {
          ...localCourseData,
          [selectedStructureType]: [
            ...(localCourseData[selectedStructureType] || []),
            { ...formData, awardIds },
          ],
        };

    try {
      const newCourseData = await courseService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: editLocation,
        course: updatedCourse,
        sourceClassroomId: classroomId,
      });
      setLocalCourseData(newCourseData);
      await refreshEditData();

      const newItems = newCourseData[selectedStructureType] || [];
      // eslint-disable-next-line unicorn/prefer-at
      const lastCreated = newItems[newItems.length - 1];
      if (lastCreated?.id) setNewlyCreatedItemId(lastCreated.id);

      addNotification({
        type: NotificationType.success,
        message:
          selectedStructureType === "units"
            ? "Unit created."
            : "Lesson created.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message:
          selectedStructureType === "units"
            ? "Error creating unit."
            : "Error creating lesson.",
      });
    } finally {
      setIsSaving(false);
      setParentId();
      setShowCreateStructureModal(false);
    }
  };

  const onApplyUnitChanges = async (formData) => {
    setIsSaving(true);
    const awardIds = formData.awardIds?.id
      ? [{ id: formData.awardIds.id, type: formData.awardType }]
      : [];
    try {
      const updated = await courseService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditUnitModal.id,
        sourceClassroomId: classroomId,
        course: {
          ...localCourseData,
          units: localCourseData.units.map((unit) =>
            unit.id === selectedStructureData.id
              ? {
                  ...unit,
                  title: formData.title,
                  awardIds,
                  lessons: formData.lessons || [],
                }
              : unit
          ),
        },
      });
      setLocalCourseData(updated);
      await refreshEditData();
      addNotification({
        type: NotificationType.success,
        message: "Unit updated.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Error updating unit.",
      });
    } finally {
      setIsSaving(false);
      setShowEditUnitModal(false);
      setSelectedStructureData();
    }
  };

  const onApplyLessonChanges = async (formData) => {
    setIsSaving(true);
    const awardIds = formData.awardIds?.id
      ? [{ id: formData.awardIds.id, type: formData.awardType }]
      : [];
    const activities = (formData.activities || [])
      .flat()
      .map((a) => {
        const data = a?.data || a;
        if (!data) return;
        if (!data.contentId) data.contentId = data.id;
        return data;
      })
      .filter(Boolean);
    formData.activities = activities;

    const updatedCourse =
      courseMainStructure === "lessons"
        ? {
            ...localCourseData,
            lessons: localCourseData.lessons.map((lesson) =>
              lesson.id === selectedStructureData.id
                ? { ...lesson, title: formData.title, awardIds, activities }
                : lesson
            ),
          }
        : {
            ...localCourseData,
            units: localCourseData.units.map((unit) => ({
              ...unit,
              lessons: unit.lessons.map((lesson) =>
                lesson.id === selectedStructureData.id
                  ? { ...lesson, title: formData.title, awardIds, activities }
                  : lesson
              ),
            })),
          };

    try {
      const updated = await courseService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditLessonModal.id,
        course: updatedCourse,
        sourceClassroomId: classroomId,
      });
      setLocalCourseData(updated);
      await refreshEditData();
      addNotification({
        type: NotificationType.success,
        message: "Lesson updated.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Error updating lesson.",
      });
    } finally {
      setIsSaving(false);
      setShowEditLessonModal(false);
      setSelectedStructureData();
    }
  };

  const handleDelete = (entityType, entityId, entityName) => {
    setDeleteState({ open: true, entityType, entityId, entityName });
  };

  const onHandleDelete = async () => {
    const { entityType, entityId } = deleteState;
    if (!entityId || !entityType) return;
    setIsSaving(true);
    try {
      if (entityType === "unit") {
        await courseService.deleteUnit(entityId, courseId);
        setLocalCourseData((prev) => ({
          ...prev,
          units: prev.units.filter((u) => u.id !== entityId),
        }));
      } else if (entityType === "lesson") {
        await courseService.deleteLesson(entityId, courseId);
        setLocalCourseData((prev) => {
          if (prev.lessons?.some((l) => l.id === entityId)) {
            return {
              ...prev,
              lessons: prev.lessons.filter((l) => l.id !== entityId),
            };
          }
          return {
            ...prev,
            units: prev.units.map((unit) => ({
              ...unit,
              lessons: unit.lessons.filter((l) => l.id !== entityId),
            })),
          };
        });
      }
      addNotification({
        type: NotificationType.success,
        message: `${entityType} deleted successfully.`,
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: `Error deleting ${entityType}.`,
      });
    } finally {
      setIsSaving(false);
      setDeleteState({ open: false });
    }
  };

  const handleResetCourse = async () => {
    setIsSaving(true);
    try {
      const updated = await courseService.sendProgramInfo({
        editedBy: currentUser.userId,
        editedFrom: EditLocation.EditProgramStructureOrderModal.id,
        course: { ...localCourseData, units: [], lessons: [] },
        sourceClassroomId: classroomId,
      });
      setLocalCourseData(updated);
      setCourseMainStructure();
      setShowInitialView(true);
      await refreshEditData();
      addNotification({
        type: NotificationType.success,
        message: "Course reset.",
      });
    } catch {
      addNotification({
        type: NotificationType.error,
        message: "Error resetting course.",
      });
    } finally {
      setIsSaving(false);
      setShowResetModal(false);
    }
  };

  return (
    <Tab name={name} className={className}>
      {loading || fetchingAICurriculumFlag ? (
        <Loader />
      ) : (
        <>
          {isSaving && <Loader overlay />}
          <ProgramStructureSection
            data={localCourseData}
            canEdit={!readOnly}
            courseMainStructure={courseMainStructure}
            setCourseMainStructure={setCourseMainStructure}
            onModifyCourseStructure={onModifyCourseStructure}
            setParentId={setParentId}
            newlyCreatedItemId={newlyCreatedItemId}
            setNewlyCreatedItemId={setNewlyCreatedItemId}
            handleDelete={handleDelete}
            onAddActivity={onAddActivity}
            activityContentTypes={activityContentTypes}
            onResetCourse={() => setShowResetModal(true)}
            setShowActivityLogModal={setShowActivityLogModal}
            initialStateView={
              aiCurriculumEnabled &&
              showInitialView && (
                <ClassrooomCourseBuilderGuard
                  orgCode={classroomOrgCode}
                  classroomId={classroomId}
                  hasExistingCourse={!!courseId}
                  onCourseBuilt={onCourseBuilt}
                  onBuildYourOwn={() => setShowInitialView(false)}
                />
              )
            }
          />
          {!readOnly && (
            <>
              <CreateCourseStructureModal
                open={showCreateStructureModal}
                onClose={() => {
                  setParentId();
                  setShowCreateStructureModal(false);
                }}
                onCreateCourseStructure={onCreateCourseStructure}
                selectedStructureType={selectedStructureType}
                loading={isSaving}
                showAwards={false}
                preventClose
              />
              <EditProgramStructureOrderModal
                loading={isSaving}
                items={
                  courseMainStructure === "units"
                    ? localCourseData?.units
                    : localCourseData?.lessons
                }
                open={showEditStructureOrderModal}
                onClose={() => setShowEditStructureOrderModal(false)}
                onApplyChanges={onApplyStructureOrderChanges}
                confirmRequired={false}
                structureType={selectedStructureType}
                preventClose
                async
              />
              {showEditUnitModal && (
                <EditUnitModal
                  open={showEditUnitModal}
                  onClose={() => {
                    setShowEditUnitModal(false);
                    setSelectedStructureData();
                  }}
                  onApplyChanges={onApplyUnitChanges}
                  async
                  unit={selectedStructureData}
                  lessons={selectedStructureData?.lessons}
                  confirmRequired={false}
                  showAwards={false}
                  preventClose
                />
              )}
              {showEditLessonModal && (
                <EditLessonModal
                  open={showEditLessonModal}
                  onClose={() => {
                    setShowEditLessonModal(false);
                    setSelectedStructureData();
                  }}
                  onApplyChanges={onApplyLessonChanges}
                  async
                  lesson={selectedStructureData}
                  confirmRequired={false}
                  selectedActivities={selectedStructureData?.activities}
                  showAwards={false}
                  preventClose
                />
              )}
              {showAddActivityModal && (
                <AddActivityModal
                  open={showAddActivityModal}
                  onClose={() => {
                    setShowAddActivityModal(false);
                    setAddActivityLesson();
                    setAddActivityContentType();
                  }}
                  onApplyChanges={onAddActivities}
                  async
                  contentType={addActivityContentType}
                  allActivities={getMappedActivitiesForLesson(
                    addActivityLesson?.activities
                  )}
                  preventClose
                />
              )}
              <DeletionModal
                open={deleteState.open}
                onCancel={() => setDeleteState({ open: false })}
                onDeleteClick={onHandleDelete}
                entityType={deleteState.entityType}
                name={deleteState.entityName}
                preventClose
              />
              <ResetCourseModal
                open={showResetModal}
                onCancel={() => setShowResetModal(false)}
                onResetClick={handleResetCourse}
                preventClose
              />
            </>
          )}
          {showActivityLogModal && (
            <ProgramActivityLogModal
              open={showActivityLogModal}
              onClose={() => setShowActivityLogModal(false)}
              programId={courseId}
              logs={activityLogs}
              loading={activityLogsLoading}
              getActivityLogs={getCourseActivityLogs}
            />
          )}
        </>
      )}
    </Tab>
  );
};
